import { createAction, ActionType } from 'typesafe-actions';
import { DataFetchingThunkAction, BellaThunkDispatch, StateInterface } from '../types';
import { getErrorCustomData } from '../../../common/errors/CustomFetchApiError';
import retry from '../../../common/utils/retry';
import { RetryError } from '../../../common/errors';
import { WishListNotificationMessages } from '../../components/ProductDetailPage/hooks/useWishlist';
import { addNotification, defaultNotificationShowInterval } from '../notifications/actions';
import { DisplayType } from '../notifications/types';
import {
    WishlistResponse,
    FETCH_WISHLIST,
    FETCH_WISHLIST_SUCCESS,
    FETCH_WISHLIST_FAILED,
    ADD_TO_WISHLIST,
    ADD_TO_WISHLIST_SUCCESS,
    ADD_TO_WISHLIST_FAILED,
    REMOVE_FROM_WISHLIST,
    REMOVE_FROM_WISHLIST_FAILED,
    REMOVE_FROM_WISHLIST_SUCCESS,
    WISHLIST_QUEUE_UPDATED,
    ADD,
    REMOVE,
} from './types';

const isFetchingWishListAction = createAction(FETCH_WISHLIST);

const fetchWishlistSuccessAction = createAction(FETCH_WISHLIST_SUCCESS, action => (response: WishlistResponse) =>
    action({
        response,
    })
);

const fetchWishlistFailedAction = createAction(FETCH_WISHLIST_FAILED, action => (error: Error) =>
    action({
        error,
    })
);

export const fetchWishlistAction = (): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    dispatch(isFetchingWishListAction());

    try {
        const getWishList = retry(() => apiService.shopApi.getWishList(), 3);
        const wishListResponse = await getWishList();
        dispatch(fetchWishlistSuccessAction(wishListResponse));
    } catch (err) {
        const errorInfo = RetryError.isRetryError(err) ? err.toJson() : getErrorCustomData(err);
        logger.error(err, { action: 'fetchWishlistFailedAction', ...errorInfo });
        dispatch(fetchWishlistFailedAction(err));
    }
};

const isAddingToWishListAction = createAction(ADD_TO_WISHLIST, action => (simpleSku: string) =>
    action({
        simpleSku,
    })
);

const addToWishlistSuccessAction = createAction(ADD_TO_WISHLIST_SUCCESS);

const addToWishlistFailedAction = createAction(ADD_TO_WISHLIST_FAILED, action => (error: Error, simpleSku: string) =>
    action({
        error,
        simpleSku,
    })
);

const isRemovingFromWishListAction = createAction(REMOVE_FROM_WISHLIST, action => (simpleSku: string) =>
    action({
        simpleSku,
    })
);

const removeFromWishlistSuccessAction = createAction(REMOVE_FROM_WISHLIST_SUCCESS);

const removeFromWishlistFailedAction = createAction(
    REMOVE_FROM_WISHLIST_FAILED,
    action => (error: Error, simpleSku: string) =>
        action({
            error,
            simpleSku,
        })
);

export const updateWishlistQueue = createAction(
    WISHLIST_QUEUE_UPDATED,
    action => (todo: string | null, simpleSku: string | null) =>
        action({
            todo,
            simpleSku,
        })
);

export const actions = {
    isFetchingWishListAction,
    fetchWishlistSuccessAction,
    fetchWishlistFailedAction,
    isAddingToWishListAction,
    addToWishlistSuccessAction,
    addToWishlistFailedAction,
    isRemovingFromWishListAction,
    removeFromWishlistFailedAction,
    updateWishlistQueue,
    removeFromWishlistSuccessAction,
};

export const addToWishlistAction = (
    simpleSku: string,
    notificationMessages?: WishListNotificationMessages
): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    dispatch(isAddingToWishListAction(simpleSku));
    try {
        await apiService.shopApi.addToWishList(simpleSku);
        dispatch(addToWishlistSuccessAction());
        if (notificationMessages) {
            dispatch(addNotification(notificationMessages.successMessage));
        }
    } catch (err) {
        logger.error(err, { action: 'addToWishlistFailedAction', ...getErrorCustomData(err) });
        dispatch(addToWishlistFailedAction(err, simpleSku));
        if (notificationMessages) {
            dispatch(
                addNotification(notificationMessages.errorMessage, defaultNotificationShowInterval, DisplayType.error)
            );
        }
    }
};

export const removeFromWishlistAction = (simpleSku: string): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    dispatch(isRemovingFromWishListAction(simpleSku));
    try {
        await apiService.shopApi.removeFromWishList(simpleSku);
        dispatch(removeFromWishlistSuccessAction());
    } catch (err) {
        logger.error(err, { action: 'removeFromWishlistFailedAction', ...getErrorCustomData(err) });
        dispatch(removeFromWishlistFailedAction(err, simpleSku));
    }
};

export const fetchOrAddWishlist = () => (dispatch: BellaThunkDispatch, getState: () => StateInterface) => {
    const { wishlistQueue } = getState();
    if (wishlistQueue.simpleSku) {
        if (wishlistQueue.todo === ADD) {
            dispatch(addToWishlistAction(wishlistQueue.simpleSku));
        } else if (wishlistQueue.todo === REMOVE) {
            dispatch(removeFromWishlistAction(wishlistQueue.simpleSku));
        }
        dispatch(updateWishlistQueue(null, null));
    } else {
        dispatch(fetchWishlistAction());
    }
};

export type WishListActionType = ActionType<typeof actions>;
